<template>
  <div class="page-login-v3 layout-full th">
    <div class="page vertical-align text-xs-center" >
      <div class="page-content vertical-align-middle">
        <div class="brand">
          <img class="brand-img" src="/static/images/byteark-loading-white.png" alt="logo account" width="180">
          <h2 class="brand-text font-size-24 grey-100"></h2>
        </div>
        <div class="panel">
          <div class="panel-body">
            <div class="card-stat" v-if="isLoading">
              <loading-indicator></loading-indicator>
            </div>
            <div v-else>
              <team-list-form
                key="namespace-login"
                v-bind:teams="teams"
                v-on:selectTeam="submitOauthForm"
                v-on:loginOtherTeam="onLoginOtherTeam"
                v-if="this.step === this.stepEnum.teamList">
              </team-list-form>
              <div v-else>
                <form v-on:submit.prevent="onSubmit">
                  <transition name="fade-in" mode="out-in" appear>
                    <div key="team-login" class="team-form" v-if="this.step === this.stepEnum.namespaceForm">
                      <h3 class="title title-login">{{ $t('login.action.enterTeamURL') }}</h3>
                      <form-group v-bind:error="formErrors.first('namespace')">
                        <div class="input-group">
                          <div class="input-group-addon form-group-mobile">{{`${baseURL}/`}}</div>
                          <input type="text" class="form-control form-group-mobile" id="namespace" placeholder="Team URL" v-model="user.namespace">
                        </div>
                      </form-group>
                      <button type="submit" class="btn btn-primary btn-block btn-lg m-t-40">
                        {{ $t('action.continue') }} <span class="glyphicon glyphicon-chevron-right"></span>
                      </button>
                      <div class="login-help">
                        <p class="text-center">{{ $t('login.labels.dontHaveAccount') }} <u><router-link to="register" class="btn btn-link btn-register">{{ $t('login.action.registerNewAccount') }}</router-link></u></p>
                      </div>
                    </div>
                    <div key="user-login" class="user-form" v-else-if="this.step === this.stepEnum.loginForm">
                      <h3 class="title title-login">{{ $t('login.labels.prefixLogin') }} <span class="text-uppercase blue-600">{{user.namespace}}</span> {{ $t('login.labels.subfixLogin') }}</h3>
                      <alert v-show="isLoginFailed" alertClass="alert-dismissible" type="danger">
                        <span>{{loginFailedText}}</span>
                      </alert>
                      <form-group v-bind:error="formErrors.first('email')">
                        <label for="email">{{ $t('account.labels.email') }}</label>
                        <input type="email" class="form-control" id="email" :placeholder="$t('account.labels.email')" v-model="user.email" ref="emailInput">
                      </form-group>
                      <form-group v-bind:error="formErrors.first('password')">
                        <label for="password">{{ $t('account.labels.password') }}</label>
                        <input type="password" class="form-control" id="password" :placeholder="$t('account.labels.password')" v-model="user.password">
                      </form-group>
                      <button type="submit" class="btn btn-block btn-primary btn-lg btn-submit" v-bind:disabled="isLoggingIn" v-bind:class="{ 'btn-default': isLoggingIn }">
                        <div class="loader vertical-align-middle loader-circle loader-form" v-if="isLoggingIn"></div> {{ $t('action.login') }}
                      </button>
                      <div class="login-help">
                        <router-link v-bind:to="`/${this.user.namespace}/forgot`" class="btn btn-link btn-block pull-right">{{ $t('forgot.action.forgotPassword') }}</router-link>
                        <p class="text-center"><strong>{{ $t('login.labels.dontHaveAccountOnTeam') }}</strong><br/>{{ $t('login.labels.contactTeamAdmin') }}</p>
                      </div>
                    </div>
                  </transition>
                </form>
              </div>
            </div>
            <form method="POST" v-bind:action="oauthFormURL" ref="oauthForm">
              <input type="hidden" name="access_token" v-model="accessToken">
            </form>
          </div>
        </div>
        <footer class="page-copyright page-copyright-inverse">
          <p>Service by ByteArk</p>
        </footer>
      </div>
    </div>
  </div>
</template>

<script>
import qs from 'qs';
import { Validator } from 'vee-validate';
import FormGroup from 'components/forms/FormGroup';
import Alert from 'components/Alert';
import TeamListForm from 'components/login/TeamListForm';
import LoadingIndicator from 'components/LoadingIndicator';
import AuthManager from 'src/managers/AuthManager';

export default {
  components: {
    Alert,
    FormGroup,
    TeamListForm,
    LoadingIndicator,
  },
  watch: {
    'user.namespace': function() {
      if (this.user.namespace) {
        this.user.namespace = this.user.namespace.toLowerCase();
      }

      if (this.formErrors.has('namespace')) {
        this.formErrors.remove('namespace');
      }
    },
    'user.email': function() {
      if (this.formErrors.has('email')) {
        this.formErrors.remove('email');
      }
    },
    'user.password': function() {
      if (this.formErrors.has('password')) {
        this.formErrors.remove('password');
      }
    },
  },
  computed: {
    baseURL: function() {
      return process.env.BASE_URL;
    },
    oauthFormURL: function() {
      return this.authManager.getFormAuthorizeURL(this.$route.query);
    },
    getLoginPath: function() {
      if (Object.keys(this.$route.query).length > 0) {
        // append query string to login url
        return `/${this.user.namespace}/login?${qs.stringify(this.$route.query)}`;
      } else {
        // no query string in url
        return `/${this.user.namespace}/login`;
      }
    },
  },
  data: function () {
    return {
      stepEnum: {
        teamList: 0,
        namespaceForm: 1,
        loginForm: 2,
      },
      step: 0,
      authManager: null,
      validator: null,
      formErrors: null,
      isLoading: false,
      isLoggingIn: false,
      isLoginFailed: false,
      loginFailedText: this.$i18n.t('login.response.failed'),
      teams: null,
      user: {
        namespace: null,
        email: null,
        password: null,
      },
      accessToken: null,
    };
  },
  created: function() {
    this.authManager = new AuthManager();

    this.validator = new Validator();
    this.validator.attach('namespace', 'required|namespace|min:3|max:32', { prettyName: 'Team URL' });
    this.validator.attach('email', 'required|email');
    this.validator.attach('password', 'required');

    this.$set(this, 'formErrors', this.validator.errorBag);

    this.isLoginFailed = false;
    this.step = this.stepEnum.namespaceForm;

    if (this.$route.params.namespace) {
      // for route /:namespace/login
      this.user.namespace = this.$route.params.namespace;
      this.step = this.stepEnum.loginForm;

      // Try to get access token, if it has, we'll just kick the user
      if (this.authManager.isOauthFromOtherService(this.$route.query)) {
        this.authManager.getAccessToken(this.$route.params.namespace).then((credential) => {
          if (!credential || !credential.access_token) {
            return;
          }

          this.isLoading = true;
          this.accessToken = credential.access_token;
          this.$nextTick(() => {
            this.$refs.oauthForm.submit();
          });
        });
      }
    } else {
      this.isLoading = true;

      // for route /login
      this.authManager.getLoggedInTeams().then((teams) => {
        if (teams.length > 0) {
          // show team list
          this.teams = teams;
          this.step = this.stepEnum.teamList;
        } else {
          // doesn't have logged in team, ask user to input team namespace
          this.step = this.stepEnum.namespaceForm;
        }

        this.isLoading = false;
      });
    }
  },
  methods: {
    onSubmit: function () {
      if (this.step === this.stepEnum.namespaceForm) {
        this.validator.validateAll({
          namespace: this.user.namespace,
        })
        .then((success) => {
          if (!success) {
            return;
          }

          this.step = this.stepEnum.loginForm;
          this.$router.push(this.getLoginPath());
        }).catch(() => {});
      } else if (this.step === this.stepEnum.loginForm) {
        this.validator.validateAll({
          namespace: this.user.namespace,
          email: this.user.email,
          password: this.user.password,
        })
        .then((success) => {
          if (!success) {
            return;
          }

          this.isLoggingIn = true;

          return this.authManager.login(
            this.user.email,
            this.user.password,
            this.user.namespace);
        })
        .then(() => {
          this.isLoginFailed = false;
          this.isLoggingIn = false;

          if (this.authManager.isOauthFromOtherService(this.$route.query)) {
            // get access_token and submit oauth request form
            this.authManager.getAccessToken(this.user.namespace).then(this.submitOauthForm);
          } else {
            // redirect to dashboard
            this.$router.push(`/${this.user.namespace}`);
          }
        })
        .catch((error) => {
          if (error.response) {
            // error from axios
            if (error.response.status === 403) {
              this.loginFailedText = error.response.data.message;
            } else {
              this.loginFailedText = this.$i18n.t('login.response.failed');
            }

            this.isLoginFailed = true;
            this.user.password = null;
            this.isLoggingIn = false;
          }
        });
      }
    },
    onLoginOtherTeam: function() {
      this.step = this.stepEnum.namespaceForm;
    },
    submitOauthForm: function(credential) {
      this.accessToken = credential.access_token;
      this.$nextTick(() => {
        this.$refs.oauthForm.submit();
      });
    },
    clearFormErrors() {
      this.formErrors.clear();
    },
  },
};
</script>

<style lang="scss" scoped>
.alert {
  margin-bottom: 33px;
}
.page-login-v3 {
  form {
    margin: 0;
  }
}

.login-help {
  margin-top: 30px;

  a {
    margin-left: 0px;
  }
}
</style>



// WEBPACK FOOTER //
// Login.vue?18802de8